import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as dateFns from 'date-fns';
import { Button, InputNumber, Segmented, Switch, Tooltip } from 'antd';
import { X } from 'lucide-react';
import { useAuth } from '../../../../providers/AuthProvider';
import { CommitmentOptions } from '../../../talent-preferences/components/DashboardPreferences';
import DashboardCard, { LastUpdatedPill } from './DashboardCard';
import { getAvailabilityStatus } from './availabilityStatus';

/**
 * Availability & Work Status card for the new talent dashboard.
 * Functional / Tailwind port of AvailabilityPanel + DashboardPreferences + Calendar.
 */

const MAX_WEEKLY_HOURS = 40;
const CALENDAR_MONTHS = 4;

// Short copy for the work-status row (full copy still lives in CommitmentOptions)
const COMMITMENT_LABELS = {
    1: { title: 'Side Hustler', summary: 'Freelancing alongside a full-time job' },
    3: { title: 'Full-Time Freelancer', summary: 'Freelance is my primary income' },
};

const PREFERENCE_FIELDS = ['is_available', 'talent_commitment_level', 'has_contract_to_hire_interest', 'has_full_time_employment_interest'];

const SEGMENTED_CLASSES = [
    'rounded-lg bg-gray-200 p-1',
    '[&_.ant-segmented-item]:rounded-md',
    '[&_.ant-segmented-item-label]:min-h-[30px] [&_.ant-segmented-item-label]:px-4 [&_.ant-segmented-item-label]:text-xs [&_.ant-segmented-item-label]:font-semibold [&_.ant-segmented-item-label]:leading-[30px] [&_.ant-segmented-item-label]:text-gray-500',
    '[&_.ant-segmented-item-selected_.ant-segmented-item-label]:text-primary',
].join(' ');

const WEEK_INPUT_CLASSES = [
    'w-14 rounded-lg border-gray-200',
    '[&_input]:h-9 [&_input]:text-center [&_input]:text-sm [&_input]:font-semibold [&_input]:text-gray-900',
    '[&.ant-input-number-disabled]:bg-white [&.ant-input-number-disabled_input]:cursor-default [&.ant-input-number-disabled_input]:text-gray-900',
].join(' ');

const pickSchedule = (user) => ({
    available_hours_per_week: user?.available_hours_per_week ?? null,
    work_schedule_weeks: { ...(user?.work_schedule_weeks || {}) },
});

// Same week keys / rules as Calendar.renderCells()
const buildCalendarWeeks = (month) => {
    const monthStart = dateFns.startOfMonth(month);
    const endDate = dateFns.endOfWeek(dateFns.endOfMonth(monthStart));
    const weeks = [];

    let day = dateFns.startOfWeek(monthStart);

    while (day <= endDate) {
        const days = [];
        for (let i = 0; i < 7; i++) {
            days.push(day);
            day = dateFns.addDays(day, 1);
        }

        const week = dateFns.startOfISOWeek(day);
        const firstDayInMonth = TB.find(days, d => dateFns.isSameMonth(d, monthStart)) || days[0];

        weeks.push({
            days,
            week,
            key: TB.moment(week).utcOffset('+00:00', true).format('YYYY-MM-DDTHH:mm:ssZ'),
            label: dateFns.format(firstDayInMonth, 'MMM d'),
            isPast: dateFns.isPast(dateFns.endOfISOWeek(day)) || !dateFns.isSameMonth(monthStart, week),
        });
    }

    return weeks;
};

const WeekHoursInput = ({ value, placeholder, disabled, onChange, onFocus, onBlur, ariaLabel }) => (
    <InputNumber
        aria-label={ariaLabel}
        className={WEEK_INPUT_CLASSES}
        controls={false}
        min={0}
        max={MAX_WEEKLY_HOURS}
        precision={0}
        inputMode="numeric"
        value={TB.isNaN(parseInt(value)) ? null : parseInt(value)}
        placeholder={TB.isDefined(placeholder) && placeholder !== null ? String(placeholder) : ''}
        disabled={disabled}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={() => {
            if (TB.isNaN(parseInt(value))) onChange(0);
            onBlur?.();
        }}
    />
);

/* ----------------------------------------------------------------------------
 * Preference rows (Open to Work / Work status / Perm roles)
 * ------------------------------------------------------------------------- */

const PreferenceRow = ({ label, control, description }) => (
    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 py-2.5 first:pt-0">
        <p className="m-0 w-full text-sm font-semibold text-gray-900 sm:w-44 sm:shrink-0">{label}</p>
        <div className="shrink-0">{control}</div>
        {description && <div className="min-w-0 flex-1 text-xs text-gray-500">{description}</div>}
    </div>
);

const WorkPreferences = ({ auth, onChange, isDisabled }) => {
    const preferences = TB.pick(TB.cloneDeep(auth), PREFERENCE_FIELDS);
    const isApproved = auth.isProfileApproved?.();
    const commitmentLevel = Number(preferences.talent_commitment_level);

    const profileStatusMessage = useMemo(() => {
        if (auth.isProfileInProgress?.()) return 'Complete your profile to submit for approval';
        if (auth.isProfileReadyForApproval?.()) return 'Submit your profile for Wripple to review';
        if (auth.isPendingProfileApproved?.()) return 'Profile will be visible after Wripple approves your submission';
        if (auth.isProfileApproved?.()) return null;
        if (auth.latest_appeal?.is_rejected) return 'Appeal was not approved — you may submit a new appeal';
        if (auth.latest_appeal) return 'Your appeal is under review by the Wripple team';
        if (auth.isProfileRejected?.()) return 'Profile was not approved — submit an appeal for review';
        return null;
    }, [auth]);

    const handleChange = params => onChange({ ...preferences, ...params });

    const onToggleAvailable = async (value) => {
        if (!value) {
            if (preferences.talent_commitment_level === 3 && !TB.isProdEnv()) {
                const confirmVisibility = await TB.alert('warning', 'Profile Visibility',
                    'Setting your profile Side Hustler will allow you to still be found on the platform but will limit job notifications. This will keep your Profile Visibility on with limited alerts.',
                    [
                        { title: 'Yes, change to Side Hustler' },
                        { title: 'No, turn off my profile' },
                    ]
                );

                if (confirmVisibility) {
                    return handleChange({ talent_commitment_level: 1 });
                }

                await TB.wait(400);
            }

            const confirm = await TB.sysAlert('talent.turn_off_availability_warning');
            if (!confirm) return;
        }

        return handleChange({ is_available: value });
    };

    const commitmentOptions = Object.keys(CommitmentOptions).map(key => {
        const level = Number(key);
        const title = COMMITMENT_LABELS[level]?.title || CommitmentOptions[key].title;

        return {
            value: level,
            label: (
                <Tooltip title={level === commitmentLevel ? null : CommitmentOptions[key].description} placement="top" color="#000">
                    <span>{title}</span>
                </Tooltip>
            ),
        };
    });

    const permValue = preferences.has_full_time_employment_interest === true
        ? 'yes'
        : preferences.has_full_time_employment_interest === false ? 'no' : undefined;

    return (
        <div className="divide-y divide-gray-200">
            <PreferenceRow
                label="Open to Work"
                control={(
                    <Switch
                        checked={!!preferences.is_available}
                        onChange={onToggleAvailable}
                        disabled={isDisabled}
                        className={preferences.is_available ? 'bg-[#3E8E5E]' : 'bg-gray-300'}
                    />
                )}
                description={(
                    <>
                        {preferences.is_available ? (
                            <span><span className="font-semibold text-[#3E8E5E]">Live</span> · Clients can find me and invite me to projects</span>
                        ) : (
                            <span><span className="font-semibold text-gray-700">Paused</span> · {isApproved ? 'Your profile will not show up on the Wripple platform' : "You're not open to new projects"}</span>
                        )}
                        {!isApproved && profileStatusMessage && (
                            <span className="mt-0.5 block text-xs text-amber-600">
                                <i className="fas fa-info-circle mr-1" aria-hidden="true" />
                                {profileStatusMessage}
                            </span>
                        )}
                    </>
                )}
            />

            <PreferenceRow
                label="Work status"
                control={(
                    <Segmented
                        className={SEGMENTED_CLASSES}
                        options={commitmentOptions}
                        value={commitmentLevel || undefined}
                        disabled={isDisabled}
                        onChange={value => value !== commitmentLevel && handleChange({ talent_commitment_level: value })}
                    />
                )}
                description={COMMITMENT_LABELS[commitmentLevel]?.summary}
            />

            <PreferenceRow
                label="Open to perm roles?"
                control={(
                    <Segmented
                        className={SEGMENTED_CLASSES}
                        options={[{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }]}
                        value={permValue}
                        disabled={isDisabled}
                        onChange={value => handleChange({ has_full_time_employment_interest: value === 'yes' })}
                    />
                )}
                description={preferences.has_full_time_employment_interest
                    ? 'Interested in contract-to-hire'
                    : 'Not interested in contract-to-hire'}
            />
        </div>
    );
};

/* ----------------------------------------------------------------------------
 * Calendar
 * ------------------------------------------------------------------------- */

const AvailabilityCalendar = ({ schedule, isEditing, isLoading, readOnly, onWeekChange }) => {
    const months = useMemo(() => {
        const start = dateFns.startOfMonth(new Date());
        return TB.range(CALENDAR_MONTHS).map(index => dateFns.addMonths(start, index));
    }, []);

    const [monthIndex, setMonthIndex] = useState(0);
    const [focusWeek, setFocusWeek] = useState(null);

    const currentMonth = months[monthIndex];
    const weeks = useMemo(() => buildCalendarWeeks(currentMonth), [currentMonth]);
    const today = new Date();
    const dayLabels = TB.range(7).map(i => dateFns.format(dateFns.addDays(dateFns.startOfWeek(currentMonth), i), 'iiiii'));

    const stepMonth = step => setMonthIndex(index => (index + step + months.length) % months.length);

    const arrowClasses = 'flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 hover:border-primary hover:text-primary disabled:opacity-50';

    return (
        <div className="flex flex-col lg:flex-row">
            {/* Month grid */}
            <div className="min-w-0 flex-1 p-3">
                <div className="mb-2 flex h-10 items-center gap-1">
                    {months.map((month, index) => (
                        <button
                            key={`month-${index}`}
                            type="button"
                            onClick={() => setMonthIndex(index)}
                            className={TB.mapClass('rounded-lg border-0 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide', {
                                'bg-primary/10 text-primary': index === monthIndex,
                                'bg-transparent text-gray-500 hover:text-gray-900': index !== monthIndex,
                            })}
                        >
                            {dateFns.format(month, 'MMM')}
                        </button>
                    ))}

                    <div className="ml-auto flex gap-1.5">
                        <button type="button" aria-label="Previous month" disabled={isLoading} className={arrowClasses} onClick={() => stepMonth(-1)}>
                            <i className="fas fa-chevron-left text-xs" />
                        </button>
                        <button type="button" aria-label="Next month" disabled={isLoading} className={arrowClasses} onClick={() => stepMonth(1)}>
                            <i className="fas fa-chevron-right text-xs" />
                        </button>
                    </div>
                </div>

                <div className="grid h-8 grid-cols-7 items-center text-center text-xs font-medium text-gray-400">
                    {dayLabels.map((label, i) => <span key={`day-label-${i}`}>{label}</span>)}
                </div>

                {weeks.map(({ days, key, week }) => (
                    <div
                        key={key}
                        className={TB.mapClass('grid h-8 grid-cols-7 items-center rounded-lg text-center', {
                            'bg-primary/5': isEditing && focusWeek && dateFns.isSameWeek(days[0], focusWeek),
                        })}
                    >
                        {days.map(day => {
                            const isToday = dateFns.isSameDay(day, today);
                            const isDisabled = (dateFns.isPast(day) && !isToday) || !dateFns.isSameMonth(day, currentMonth);

                            return (
                                <span key={`day-${day}`} className="flex items-center justify-center">
                                    <span
                                        className={TB.mapClass('flex h-8 w-8 items-center justify-center rounded-full text-xs', {
                                            'bg-primary font-semibold text-white': isToday,
                                            'text-gray-300': isDisabled && !isToday,
                                            'text-gray-900': !isDisabled && !isToday,
                                        })}
                                    >
                                        {dateFns.format(day, 'd')}
                                    </span>
                                </span>
                            );
                        })}
                    </div>
                ))}
            </div>

            {/* Weekly hours */}
            <div className="border-0 border-t border-solid border-gray-200 p-3 lg:w-60 lg:shrink-0 lg:border-l lg:border-t-0">
                <p className="m-0 mb-3 text-xs leading-snug text-gray-500 lg:mb-0 lg:h-20">
                    {readOnly ? 'Hours available' : 'Hours free'} for new {laravel.platform_name} projects in{' '}
                    {dateFns.format(currentMonth, 'MMMM')}
                </p>

                {weeks.map(({ key, label, week, isPast }) => (
                    isPast ? (
                        <div key={`hours-${key}`} className="hidden h-10 lg:block" />
                    ) : (
                        <div key={`hours-${key}`} className="flex h-10 items-center justify-between gap-3">
                            <span className="text-xs text-gray-600">Week of {label}</span>

                            {isEditing && !readOnly ? (
                                <WeekHoursInput
                                    ariaLabel={`Open hours for the week of ${label}`}
                                    value={schedule.work_schedule_weeks[key]}
                                    placeholder={schedule.available_hours_per_week}
                                    disabled={!isEditing || readOnly}
                                    onChange={value => onWeekChange(key, value)}
                                    onFocus={() => setFocusWeek(week)}
                                    onBlur={() => setFocusWeek(null)}
                                />
                            ) : (
                                <span className={TB.mapClass('rounded-lg px-3 py-1 text-sm font-semibold', {
                                    'bg-gray-50 text-gray-600': isPast,
                                    'bg-green-50 text-green-700': !isPast,
                                })}>
                                    {parseInt(schedule.work_schedule_weeks[key]) || 0}
                                </span>
                            )}
                        </div>
                    )
                ))}
                {/* {weeks.map(({ key, label, week, isPast }) => (
                    <>
                        <div key={`hours-${key}`} className="flex h-10 items-center justify-between gap-3">
                            <span className="text-xs text-gray-600">Week of {label}</span>

                            {(!isEditing || readOnly || ) ? (
                                <span className={TB.mapClass('rounded-lg px-3 py-1 text-sm font-semibold', {
                                    'bg-gray-50 text-gray-600': isPast,
                                    'bg-green-50 text-green-700': !isPast,
                                })}>
                                    {parseInt(schedule.work_schedule_weeks[key]) || 0}
                                </span>
                            ) : (
                                <WeekHoursInput
                                    ariaLabel={`Open hours for the week of ${label}`}
                                    value={schedule.work_schedule_weeks[key]}
                                    placeholder={schedule.available_hours_per_week}
                                    disabled={!isEditing || readOnly}
                                    onChange={value => onWeekChange(key, value)}
                                    onFocus={() => setFocusWeek(week)}
                                    onBlur={() => setFocusWeek(null)}
                                />
                            )}

                        </div>
                    </>
                ))} */}
            </div>
        </div>
    );
};

const ExpirationOverlay = ({ isExpired, message, readOnly, isLoading, onConfirm, onDismiss }) => (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-gray-900/30 p-4">
        <div className="relative w-full max-w-sm rounded-xl bg-white p-6 text-center shadow-lg">
            {!isExpired && (
                <button
                    type="button"
                    aria-label="Dismiss"
                    onClick={onDismiss}
                    className="absolute right-3 top-3 border-0 bg-transparent p-1 text-gray-400 hover:text-gray-700"
                >
                    <X className="h-4 w-4" />
                </button>
            )}

            <div className="mb-2 flex items-center justify-center gap-2">
                <i className="fas fa-exclamation-triangle text-danger" aria-hidden="true" />
                <h3 className="m-0 text-sm font-semibold text-gray-900">
                    {isExpired ? 'Availability Expired' : 'Availability Expiring Soon'}
                </h3>
            </div>

            <p className="m-0 mb-2 text-xs text-gray-600">{message}</p>

            {!readOnly && (
                <>
                    <p className="m-0 mb-4 text-xs text-gray-500">
                        By confirming your availability, clients are more likely to reach out for current job postings and opportunities.
                    </p>
                    <Button
                        type="primary"
                        block
                        disabled={isLoading}
                        onClick={onConfirm}
                        className="h-auto whitespace-normal rounded-lg py-2.5 font-semibold"
                    >
                        I'm ready for opportunities until {TB.moment().add(1, 'month').format('MMMM D, YYYY')}
                    </Button>
                </>
            )}
        </div>
    </div>
);

/* ----------------------------------------------------------------------------
 * Card
 * ------------------------------------------------------------------------- */

const AvailabilityCard = ({
    user: userProp,
    readOnly = false,
    onResetAvailability,
    title = 'Availability & Work Status',
    className,
    inactive,
    inactiveMessage = 'Use our availability calendar to manage when you’re open for work.',
}) => {
    const { auth } = useAuth();
    const user = userProp || auth;

    const [schedule, setSchedule] = useState(() => pickSchedule(user));
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isExpiringSoonOpen, setIsExpiringSoonOpen] = useState(true);

    const busyRef = useRef(false);
    const isEditingRef = useRef(isEditing);
    isEditingRef.current = isEditing;

    // Keep the local schedule in sync with the user unless they're mid-edit
    useEffect(() => {
        if (!isEditingRef.current) setSchedule(pickSchedule(user));
    }, [user?.work_schedule_weeks, user?.available_hours_per_week]);

    if (!user) return null;

    const { expirationDate, isExpired, isExpiring, daysUntilExpiry } = getAvailabilityStatus(user);

    const expirationMessage = isExpired
        ? (user.talent_availability_confirmed_at
            ? `Your availability was last confirmed ${TB.moment(user.talent_availability_confirmed_at).from(TB.moment())}`
            : 'Your availability has not been confirmed')
        : daysUntilExpiry === 0 ? 'Your availability expires today' : 'Your availability expires soon';

    const talentUpdate = async (params) => {
        if (busyRef.current) return false;
        busyRef.current = true;
        setIsLoading(true);

        let updated = null;
        try {
            updated = await TB.auth().talentUpdate(params);
            if (updated) setSchedule(pickSchedule(updated));
        } catch (error) {
            console.warn(error);
        }

        await TB.wait(250);
        busyRef.current = false;
        setIsLoading(false);

        return updated;
    };

    const saveSchedule = async () => {
        const updated = await talentUpdate({
            available_hours_per_week: schedule.available_hours_per_week,
            work_schedule_weeks: TB.map(schedule.work_schedule_weeks, (hours, week) => ({
                started_at: week,
                available_hours: parseInt(hours) || 0,
            })),
        });

        if (updated) {
            setIsEditing(false);
            return TB.sysAlert('talent.availability_updated_success');
        }
    };

    const cancelEditing = () => {
        setIsEditing(false);
        setSchedule(pickSchedule(user));
    };

    const setWeekHours = (key, value) => setSchedule(current => ({
        ...current,
        work_schedule_weeks: { ...current.work_schedule_weeks, [key]: value },
    }));

    const syncAllWeeksToDefault = async () => {
        const confirm = await TB.alert(
            'alert',
            'Reset All Weeks?',
            'Are you sure you want to set all weeks to your default open hours per week?',
            [{ title: 'Yes, continue' }, { title: 'Cancel' }]
        );
        if (!confirm) return;

        setSchedule(current => ({
            ...current,
            work_schedule_weeks: TB.mapValues(current.work_schedule_weeks, () => current.available_hours_per_week),
        }));
    };

    const showOverlay = isExpired || (isExpiring && isExpiringSoonOpen);

    return (
        <DashboardCard
            title={title}
            className={className}
            inactive={inactive}
            inactiveMessage={inactiveMessage}
            headerRight={<LastUpdatedPill days={user.lastUpdatedWorkStatus?.()} />}
            footer={!isExpired && (
                <>
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="text-sm text-gray-900">Default open hours / week</span>
                        {isEditing && !readOnly ? (
                            <WeekHoursInput
                                ariaLabel="Default open hours per week"
                                value={schedule.available_hours_per_week}
                                onChange={value => setSchedule(current => ({ ...current, available_hours_per_week: value }))}
                            />
                        ) : (
                            <span className="rounded-lg bg-green-50 px-3 py-1 text-sm font-semibold text-green-700">
                                {parseInt(schedule.available_hours_per_week) || 0}
                            </span>
                        )}
                        {isEditing && !readOnly && (
                            <button
                                type="button"
                                disabled={isLoading}
                                onClick={syncAllWeeksToDefault}
                                className="border-0 bg-transparent p-0 text-xs font-semibold text-primary hover:text-primary-dark"
                            >
                                Set all
                                {/* Set all weeks to this default */}
                            </button>
                        )}
                    </div>

                    {!readOnly && !inactive && (
                        <div className="flex gap-2">
                            {isEditing && (
                                <Button disabled={isLoading} onClick={cancelEditing} className="h-auto rounded-lg px-5 py-2.5 font-semibold">
                                    Cancel
                                </Button>
                            )}
                            <Button
                                type="primary"
                                loading={isEditing && isLoading}
                                disabled={isLoading}
                                onClick={isEditing ? saveSchedule : () => setIsEditing(true)}
                                className="h-auto rounded-lg px-5 py-2.5 text-xs"
                            >
                                {isEditing ? 'Save changes' : 'Edit open hours'}
                            </Button>
                        </div>
                    )}
                </>
            )}
        >
            <WorkPreferences
                auth={user}
                onChange={talentUpdate}
                isDisabled={isLoading || readOnly}
            />

            <div className="relative overflow-hidden rounded-xl border border-solid border-gray-200">
                {!isExpired && expirationDate && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-0 border-b border-solid border-gray-200 bg-gray-50 px-4 py-3 sm:px-5">
                        <span className="text-xs text-gray-700">Availability confirmed until</span>
                        <span className="text-sm font-semibold text-gray-900">{expirationDate.format('MMM D, YYYY')}</span>
                        {/* <span
                            className={TB.mapClass('rounded-full px-2.5 py-0.5 text-xs font-semibold', {
                                'bg-amber-100 text-amber-700': isExpiring,
                                'bg-gray-200/70 text-gray-500': !isExpiring,
                            })}
                        >
                            Expires {expirationDate.fromNow()}
                        </span> */}
                        {!readOnly && onResetAvailability && (
                            <Button
                                size="small"
                                disabled={isLoading}
                                onClick={() => onResetAvailability()}
                                className="ml-auto h-auto text-xs rounded-lg border-primary/40 px-4 py-1.5 font-semibold text-primary"
                            >
                                Reset for 30 days
                            </Button>
                        )}
                    </div>
                )}

                <div className="relative">
                    <div className={TB.mapClass('', { 'pointer-events-none opacity-40': isExpired })}>
                        <AvailabilityCalendar
                            schedule={schedule}
                            isEditing={isEditing && !readOnly}
                            isLoading={isLoading}
                            readOnly={readOnly}
                            onWeekChange={setWeekHours}
                        />
                    </div>

                    {showOverlay && (
                        <ExpirationOverlay
                            isExpired={isExpired}
                            message={expirationMessage}
                            readOnly={readOnly}
                            isLoading={isLoading}
                            onConfirm={() => onResetAvailability?.()}
                            onDismiss={() => setIsExpiringSoonOpen(false)}
                        />
                    )}
                </div>
            </div>
        </DashboardCard>
    );
};

AvailabilityCard.propTypes = {
    user: PropTypes.object,
    readOnly: PropTypes.bool,
    onResetAvailability: PropTypes.func,
    title: PropTypes.node,
    className: PropTypes.string,
    inactive: PropTypes.bool,
    inactiveMessage: PropTypes.node,
};

export default AvailabilityCard;
